/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import {
  ActionData,
  ActionEnum,
  VirusScanFilter,
} from 'src/pages/Policy/utils/types';
import {
  FormInput,
  FormLabel,
  FormTextarea,
} from 'src/components/Common/FormComponent';
import { PolicyModeEnum } from '@Policy/utils/constants';
import { templateSelector, usePolicyContext } from '@Policy/store';
import Actions from '../Actions';
import { ActionConfig } from '@Policy/store/filters/malwareScanning';

const getActionState = (actionConfig: ActionConfig[]) => {
  const actionStatus: { [key: string]: any } = {};
  actionConfig.forEach((item: ActionConfig) => {
    actionStatus[item.actionKey] = item.action;
    actionStatus[item.notifyTypeKey] = item.notifyType;
    if (item.suspiciousTagKey) {
      actionStatus[item.suspiciousTagKey] = item.suspiciousTag;
    }
  });
  return actionStatus;
};
const reducer = (state: any, action: any) => {
  return { ...state, ...action };
};
export const UnscannableFileOptionsModal = (props: {
  isVisible: boolean;
  onClose: () => void;
  saveConfig: (config: Partial<VirusScanFilter>) => void;
  actionConfig: ActionConfig[];
  replaceText: string;
  replaceFileName: string;
}) => {
  const {
    isVisible,
    onClose,
    saveConfig,
    actionConfig,
    replaceFileName,
    replaceText,
  } = props;
  // local state
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [fileName, setFileName] = React.useState(replaceFileName);
  const [text, setText] = React.useState(replaceText);
  const [actionState, dispatch] = React.useReducer(
    reducer,
    getActionState(actionConfig)
  );
  const contextStore = usePolicyContext();
  const policySelector = templateSelector(contextStore);
  const policyMode = policySelector((s) => s.policyMode);
  const hasReplace = React.useMemo(() => {
    return actionConfig.some((item: ActionConfig) =>
      item.actions.includes(ActionEnum.replaceWithTextFile)
    );
  }, [actionConfig]);
  const actionData: ActionData[] = React.useMemo(() => {
    return actionConfig.map((item: ActionConfig) => {
      const actionItem = {
        ...item,
        handler: dispatch,
        action: actionState[item.actionKey],
        notifyType: actionState[item.notifyTypeKey],
      };
      if (item.suspiciousTagKey) {
        actionItem.suspiciousTag = actionState[item.suspiciousTagKey];
      }
      return actionItem;
    });
  }, [actionState]);
  const onSave = React.useCallback(() => {
    const config = hasReplace
      ? {
          vsReplacementFileNameUnscannable: fileName,
          vsReplacementTextUnscannable: text,
          ...actionState,
        }
      : { ...actionState };

    saveConfig(config);
    onClose();
  }, [fileName, text, actionState, hasReplace]);
  useEffect(() => {
    setFileName(replaceFileName || '');
  }, [replaceFileName]);
  useEffect(() => {
    setText(replaceText || '');
  }, [replaceText]);
  useEffect(() => {
    const newActionState = getActionState(actionConfig);
    dispatch(newActionState);
  }, [actionConfig]);
  // Request password from end user forces notification on
  useEffect(() => {
    const forcedNotify: { [key: string]: number } = {};
    actionData.forEach((item: ActionData) => {
      if (
        item.action === ActionEnum.passwordQuarantine &&
        item.notifyType !== 1
      ) {
        forcedNotify[item.notifyTypeKey] = 1;
      }
    });
    if (Object.keys(forcedNotify).length > 0) {
      dispatch(forcedNotify);
    }
  }, [actionData]);
  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={isVisible}
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent backgroundColor={colorStyle.background.secondary}>
        <ModalHeader>{t('policy.action.unScannale.title')}</ModalHeader>
        <ModalBody ml="24px" mt="16px" mb="16px">
          <Stack gap="4x">
            <Actions
              actionData={actionData}
              disabledAll={policyMode === PolicyModeEnum.DefaultMonitorPolicy}
              wrapThreatType
            />
            {actionData.some(
              (item: ActionData) => item.action === ActionEnum.pass
            ) && (
              <Flex>
                <Text size="sm" color={colorStyle.color.secondary}>
                  {t('policy.action.passDesc')}
                </Text>
              </Flex>
            )}
            {actionData.some(
              (item: ActionData) =>
                item.action === ActionEnum.replaceWithTextFile
            ) && (
              <Stack>
                <Flex mb="16px">
                  <Text>{t('policy.action.replaceAction.desc')}</Text>
                </Flex>
                <Flex mb="16px">
                  <FormLabel
                    label={t('policy.action.replaceAction.filename')}
                  />
                  <FormInput
                    placeholder={t('policy.action.replaceAction.filenameInput')}
                    value={fileName}
                    width="600px"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      console.log(e.target.value);
                      setFileName(e.target.value);
                    }}
                  />
                </Flex>
                <Flex mb="16px">
                  <FormLabel label={t('policy.action.replaceAction.text')} />
                  <FormTextarea
                    value={text}
                    width="600px"
                    onChange={(e) => {
                      console.log(e.target.value);
                      setText(e.target.value);
                    }}
                  />
                </Flex>
              </Stack>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" minWidth="20x" onClick={onSave}>
            {t('common.button.save')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
